/**
 * CartToast
 * ──────────────────────────────────────────────
 * Lightweight toast notification system for cart events.
 * No dependencies. Auto-injects its own styles + DOM.
 *
 * Usage:
 *   CartToast.show('Added to cart', 'success')
 *   CartToast.show('Out of stock', 'warning')
 *   CartToast.show('Something went wrong', 'error')
 *   CartToast.show('Item removed', 'info')
 */

class CartToast {

    static _container = null;
    static _queue = [];
    static _active = 0;
    static MAX = 3; // max visible at once
    static DURATION = 3500; // ms before auto-dismiss

    // ─────────────────────────────────────────
    // PUBLIC
    // ─────────────────────────────────────────

    /**
     * @param {string} message
     * @param {'success'|'warning'|'error'|'info'} type
     * @param {Object} options
     * @param {string}  options.title    - optional bold title
     * @param {number}  options.duration - override default duration
     * @param {boolean} options.persist  - if true, toast won't auto-dismiss
     */
    static show(message, type = 'info', options = {}) {
        CartToast._init();
        CartToast._queue.push({
            message,
            type,
            options
        });
        CartToast._flush();
    }

    static success(message, options = {}) {
        CartToast.show(message, 'success', options);
    }
    static warning(message, options = {}) {
        CartToast.show(message, 'warning', options);
    }
    static error(message, options = {}) {
        CartToast.show(message, 'error', options);
    }
    static info(message, options = {}) {
        CartToast.show(message, 'info', options);
    }

    static dismissAll() {
        CartToast._container?.querySelectorAll('.ct-toast').forEach((el) => {
            CartToast._dismiss(el);
        });
    }

    // ─────────────────────────────────────────
    // PRIVATE
    // ─────────────────────────────────────────

    static _flush() {
        while (CartToast._queue.length > 0 && CartToast._active < CartToast.MAX) {
            const {
                message,
                type,
                options
            } = CartToast._queue.shift();
            CartToast._render(message, type, options);
        }
    }

    static _render(message, type, options = {}) {
        const {
            title,
            duration = CartToast.DURATION,
            persist = false
        } = options;

        CartToast._active++;

        const toast = document.createElement('div');
        toast.className = `ct-toast ct-toast--${type}`;
        toast.setAttribute('role', 'alert');
        toast.setAttribute('aria-live', 'assertive');

        toast.innerHTML = `
      <div class="ct-toast__icon">${CartToast._icon(type)}</div>
      <div class="ct-toast__body">
        ${title ? `<span class="ct-toast__title">${title}</span>` : ''}
        <span class="ct-toast__message">${message}</span>
      </div>
      <button class="ct-toast__close" aria-label="Dismiss">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M1 1L11 11M11 1L1 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>
      <div class="ct-toast__progress"></div>
    `;

        // Close button
        toast.querySelector('.ct-toast__close').addEventListener('click', () => {
            CartToast._dismiss(toast);
        });

        CartToast._container.appendChild(toast);

        // Trigger enter animation
        requestAnimationFrame(() => {
            requestAnimationFrame(() => toast.classList.add('ct-toast--visible'));
        });

        // Progress bar + auto-dismiss
        if (!persist) {
            const bar = toast.querySelector('.ct-toast__progress');
            bar.style.animationDuration = `${duration}ms`;
            bar.classList.add('ct-toast__progress--running');

            setTimeout(() => CartToast._dismiss(toast), duration);
        }

        return toast;
    }

    static _dismiss(toast) {
        if (toast.classList.contains('ct-toast--leaving')) return;
        toast.classList.remove('ct-toast--visible');
        toast.classList.add('ct-toast--leaving');

        toast.addEventListener('transitionend', () => {
            toast.remove();
            CartToast._active = Math.max(0, CartToast._active - 1);
            CartToast._flush(); // drain queue
        }, {
            once: true
        });
    }

    static _icon(type) {
        const icons = {
            success: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
                  <path d="M5 8L7 10L11 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>`,
            warning: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 2L14.9 14H1.1L8 2Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
                  <path d="M8 6V9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  <circle cx="8" cy="11.5" r="0.75" fill="currentColor"/>
                </svg>`,
            error: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
                  <path d="M5.5 5.5L10.5 10.5M10.5 5.5L5.5 10.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>`,
            info: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
                  <path d="M8 7V11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  <circle cx="8" cy="5" r="0.75" fill="currentColor"/>
                </svg>`,
        };
        return icons[type] || icons.info;
    }

    // Inject container + styles once
    static _init() {
        if (CartToast._container) return;

        CartToast._container = document.createElement('div');
        CartToast._container.className = 'ct-container';
        CartToast._container.setAttribute('aria-label', 'Notifications');
        document.body.appendChild(CartToast._container);

        const style = document.createElement('style');
        style.textContent = `
      .ct-container {
        position: fixed;
        top: 50px;
        right: 50px;
        z-index: 99999;
        display: flex;
        flex-direction: column;
        gap: 10px;
        align-items: flex-end;
        pointer-events: none;
      }

      .ct-toast {
        position: relative;
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 14px 16px 18px;
        width: 500px;
        background: #ffffff;
        border: 1px solid #e5e5e5;
        overflow: hidden;
        pointer-events: all;
        cursor: default;
        opacity: 0;
        transform: translateY(12px) scale(0.97);
        transition: opacity 0.25s ease, transform 0.25s ease;
        font-family: 'Courier New', Courier, monospace;
      }

      .ct-toast--visible {
        opacity: 1;
        transform: translateY(0) scale(1);
      }

      .ct-toast--leaving {
        opacity: 0;
        transform: translateY(8px) scale(0.97);
        pointer-events: none;
      }

      .ct-toast::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        width: 3px;
      }

      .ct-toast--success::before { background: #1a7a4a; }
      .ct-toast--warning::before { background: #b45309; }
      .ct-toast--error::before   { background: #b91c1c; }
      .ct-toast--info::before    { background: #1d4ed8; }

      .ct-toast--success .ct-toast__icon { color: #1a7a4a; }
      .ct-toast--warning .ct-toast__icon { color: #b45309; }
      .ct-toast--error   .ct-toast__icon { color: #b91c1c; }
      .ct-toast--info    .ct-toast__icon { color: #1d4ed8; }

      .ct-toast__icon {
        flex-shrink: 0;
        margin-top: 1px;
        display: flex;
      }

      .ct-toast__body {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
      }

      .ct-toast__title {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: #111111;
      }

      .ct-toast__message {
        font-size: 11px;
        letter-spacing: 0.04em;
        color: #444444;
        line-height: 1.5;
      }

      .ct-toast__close {
        flex-shrink: 0;
        background: none;
        border: none;
        cursor: pointer;
        color: #999999;
        padding: 0;
        display: flex;
        align-items: center;
        transition: color 0.15s;
        margin-top: 1px;
      }
      .ct-toast__close:hover { color: #111111; }

      .ct-toast__progress {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 2px;
        background: #e5e5e5;
        transform-origin: left;
      }

      .ct-toast__progress--running {
        animation: ct-progress linear forwards;
      }

      @keyframes ct-progress {
        from { transform: scaleX(1); }
        to   { transform: scaleX(0); }
      }

      @media (max-width: 480px) {
        .ct-container {
          bottom: 16px;
          right: 16px;
          left: 16px;
          align-items: stretch;
        }
        .ct-toast {
          width: 100%;
        }
      }
    `;
        document.head.appendChild(style);
    }
}